import { Box, Flex, Text, Progress } from "@chakra-ui/react";
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { Row, Col } from "antd";
import Field from "ui/components/Input/Field";
import InputCep, { CepResponse } from "ui/components/Input/InputCep";
import SelectCidade from "ui/components/Input/SelectCidade";
import SelectEstado from "ui/components/Input/SelectEstado";
import { EnderecoDto } from "features/unidades/typings/unidade";
import GoogleMap from "components/GoogleMap";
import { useGoogleGeocoder } from "features/shared/useGoogleGeocoder";
import OutlineButton from "ui/components/Button/OutlineButton";
import { useQueryClient } from "react-query";

function DadosDeEndereco({
  control,
  watch,
  setField,
  required = true,
  shouldUnregister = true,
  showMap = false,
}: {
  control: Control<any, object>;
  setField: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  required?: boolean;
  shouldUnregister?: boolean;
  showMap?: boolean;
}) {
  const queryClient = useQueryClient();
  const {
    addressToCoordinates,
    coordinatesToAddress,
    loading: loadingGeocoder,
  } = useGoogleGeocoder(showMap);
  const form = watch();

  const afterLoadCep = async (data: CepResponse) => {
    setField("endereco.cidade", data.cidade);
    setField("endereco.bairro", data.bairro);
    setField("endereco.logradouro", data.rua);
    setField("endereco.estado", data.estado);
    setField("endereco.cidadeId", data.cidadeId);
    setField("endereco.estadoId", data.estadoId);

    if (showMap) {
      const coordinates = await addressToCoordinates(
        `${data.rua}, ${data.bairro}, ${data.cidade} - ${data.estado}, ${data.cep}`
      );

      if (coordinates?.latitude && coordinates?.longitude) {
        setField("endereco.latitude", coordinates.latitude);
        setField("endereco.longitude", coordinates.longitude);
      }
    }
  };

  const _endereco = form.endereco as EnderecoDto;

  const onAtualizarMapa = async () => {
    const coordinates = await addressToCoordinates(
      `${_endereco.logradouro}, ${
        _endereco.numero ? `n ${_endereco.numero}` : ""
      } ${_endereco.bairro}, ${_endereco.cep}`
    );

    if (coordinates?.latitude && coordinates?.longitude) {
      setField("endereco.latitude", coordinates.latitude);
      setField("endereco.longitude", coordinates.longitude);
    }
  };

  const onMapCoordinatesChange = async (
    latitude: number,
    longitude: number
  ) => {
    const address = await coordinatesToAddress(latitude, longitude);

    setField("endereco.latitude", latitude);
    setField("endereco.longitude", longitude);

    if (address) {
      setField("endereco.cep", address.cep);
      setField("endereco.numero", address.numero);
      setField("endereco.logradouro", address.logradouro);
      setField("endereco.bairro", address.bairro);
      setField("endereco.cidade", address.cidade);
      setField("endereco.estado", address.estado);

      const estados =
        queryClient.getQueryData<{ id: string; nome: string; uf: string }[]>(
          "/v1/estado"
        );

      const estadoId = estados?.find(
        (estado) => estado.nome === address.estado
      )?.id;

      if (estadoId) {
        setField("endereco.estadoId", estadoId);
        const cidades = queryClient.getQueryData<
          { id: string; nome: string }[]
        >(`/v1/estado/${estadoId}/cidades`);

        const cidadeId = cidades?.find(
          (cidade) => cidade.nome === address.cidade
        )?.id;

        setField("endereco.cidadeId", cidadeId);
      }
    }
  };

  const hasAnyFieldWritten = _endereco
    ? Object.values(_endereco).some((value) => !!value)
    : false;

  return (
    <>
      <Box as={Row} gutter={16} mb="32px">
        <Box as={Col} md={6}>
          <Field
            label="CEP"
            control={control}
            name="endereco.cep"
            shouldUnregister={shouldUnregister}
            render={({ field, isInvalid }) => (
              <InputCep
                {...field}
                isInvalid={isInvalid}
                onLoadCep={afterLoadCep}
              />
            )}
            rules={{
              required:
                hasAnyFieldWritten || required ? "Campo obrigatório" : false,
            }}
          />
        </Box>

        <Box as={Col} md={8}>
          <Field
            label="Rua"
            control={control}
            shouldUnregister={shouldUnregister}
            name="endereco.logradouro"
            rules={{
              required:
                hasAnyFieldWritten || required ? "Campo obrigatório" : false,
            }}
          />
        </Box>

        <Box as={Col} md={3}>
          <Field
            label="Número"
            shouldUnregister={shouldUnregister}
            control={control}
            name="endereco.numero"
            rules={{
              required:
                hasAnyFieldWritten || required ? "Campo obrigatório" : false,
            }}
          />
        </Box>

        <Box as={Col} md={7}>
          <Field
            label="Complemento"
            shouldUnregister={shouldUnregister}
            control={control}
            name="endereco.complemento"
            inputProps={{
              placeholder: "Ponto de Referência, Cor do Prédio...",
            }}
          />
        </Box>
      </Box>

      <Box as={Row} gutter={16}>
        <Box as={Col} md={8}>
          <Field
            label="Bairro"
            shouldUnregister={shouldUnregister}
            control={control}
            name="endereco.bairro"
            rules={{
              required:
                hasAnyFieldWritten || required ? "Campo obrigatório" : false,
            }}
          />
        </Box>

        <Box as={Col} md={8}>
          <Field
            label="Estado"
            shouldUnregister={shouldUnregister}
            control={control}
            name="endereco.estadoId"
            render={({ field, isInvalid }) => (
              <SelectEstado isInvalid={isInvalid} {...field} />
            )}
            rules={{
              required:
                hasAnyFieldWritten || required ? "Campo obrigatório" : false,
            }}
          />
        </Box>

        <Box as={Col} md={8}>
          <Field
            label="Cidade"
            shouldUnregister={shouldUnregister}
            control={control}
            name="endereco.cidadeId"
            render={({ field, isInvalid }) => {
              return (
                <SelectCidade
                  {...field}
                  isInvalid={isInvalid}
                  estadoId={watch("endereco.estadoId")}
                />
              );
            }}
            rules={{
              required:
                hasAnyFieldWritten || required ? "Campo obrigatório" : false,
            }}
          />
        </Box>
      </Box>

      {showMap &&
      Number.isFinite(_endereco?.latitude) &&
      Number.isFinite(_endereco?.longitude) ? (
        <Flex mt={8} flexDir="column">
          <Flex w="100%" alignItems="center">
            <Text>Latitude: {_endereco.latitude}</Text>
            <Text ml={4}>Longitude: {_endereco.longitude}</Text>
            <OutlineButton ml="auto" onClick={onAtualizarMapa}>
              Atualizar mapa
            </OutlineButton>
          </Flex>
          <Flex h="10px" w="100%" my={1}>
            {loadingGeocoder ? (
              <Progress isIndeterminate w="100%" h="3px" />
            ) : null}
          </Flex>
          <GoogleMap
            latitude={_endereco.latitude}
            longitude={_endereco.longitude}
            onCoordinatesChange={onMapCoordinatesChange}
          />
        </Flex>
      ) : null}
    </>
  );
}

export default DadosDeEndereco;
